export type CameraFacingMode = 'front' | 'rear';

export type CameraDevice = Pick<MediaDeviceInfo, 'deviceId' | 'kind' | 'label'>;

const FRONT_CAMERA_PATTERN = /front|user|face|selfie|facetime/i;
const REAR_CAMERA_PATTERN =
  /rear|back|environment|world|trase|trasera|posterior|externa/i;

const isVideoInput = (device: CameraDevice) =>
  device.kind === undefined || device.kind === 'videoinput';

const getCameraScore = (
  device: CameraDevice,
  facingMode: CameraFacingMode,
  index: number,
  totalDevices: number
) => {
  const label = device.label ?? '';
  const isFrontCamera = FRONT_CAMERA_PATTERN.test(label);
  const isRearCamera = REAR_CAMERA_PATTERN.test(label);

  if (facingMode === 'rear') {
    return (
      (isRearCamera ? 100 : 0) +
      (!isFrontCamera ? 20 : 0) +
      (isFrontCamera ? -100 : 0) +
      index
    );
  }

  return (
    (isFrontCamera ? 100 : 0) +
    (!isRearCamera ? 20 : 0) +
    (isRearCamera ? -100 : 0) +
    (totalDevices - index)
  );
};

export const choosePreferredCameraDeviceId = (
  matchingDevices: CameraDevice[],
  allDevices: CameraDevice[],
  facingMode: CameraFacingMode
) => {
  const videoDevices = allDevices.filter(isVideoInput);

  if (videoDevices.length === 0) return undefined;
  if (videoDevices.length === 1) return videoDevices[0]?.deviceId;

  const matchingVideoDevices = matchingDevices.filter(isVideoInput);
  const candidateDevices =
    matchingVideoDevices.length > 0 ? matchingVideoDevices : videoDevices;

  const rankedDevices = [...candidateDevices].sort((left, right) => {
    const leftScore = getCameraScore(
      left,
      facingMode,
      candidateDevices.indexOf(left),
      candidateDevices.length
    );
    const rightScore = getCameraScore(
      right,
      facingMode,
      candidateDevices.indexOf(right),
      candidateDevices.length
    );

    return rightScore - leftScore;
  });

  return rankedDevices[0]?.deviceId ?? videoDevices[0]?.deviceId;
};
